import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import AudioEngine from "./audioEngine";

const SUPABASE_URL = "https://trtieaijkrdipiljyzuh.supabase.co";
const SUPABASE_KEY = "sb_publishable_eJKNPdo5XwSCH-X8Ttantw_feBJed2q";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

const SYS = "#38bdf8";

const SYS_DIM = "rgba(56,189,248,0.12)";
const SYS_BORDER = "rgba(56,189,248,0.25)";
const WARN = "#ef4444";

const CAT = {
  work:     { color:"#3b82f6", bg:"rgba(59,130,246,0.10)",  icon:"💼", label:"Work" },
  personal: { color:"#a855f7", bg:"rgba(168,85,247,0.10)", icon:"🌱", label:"Personal" },
  health:   { color:"#22c55e", bg:"rgba(34,197,94,0.10)",  icon:"💪", label:"Health" },
  learning: { color:"#f59e0b", bg:"rgba(245,158,11,0.10)", icon:"📚", label:"Learning" },
  creative: { color:"#ec4899", bg:"rgba(236,72,153,0.10)", icon:"🎨", label:"Creative" },
  other:    { color:"#94a3b8", bg:"rgba(148,163,184,0.10)", icon:"⚡", label:"Other" },
};

function sanitizeName(val){ return (val||"").toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,25); }

// ─── User Replies Panel (used inside AdminDashboard messages tab) ────────────
function UserRepliesPanel(){
  const [threads,setThreads]=useState([]);
  const [loading,setLoading]=useState(true);
  const [openThread,setOpenThread]=useState(null);
  const [replyText,setReplyText]=useState("");
  const [replySending,setReplySending]=useState(false);

  useEffect(()=>{
    load();
    const poll=setInterval(()=>load(true),15000);
    return()=>clearInterval(poll);
  },[]);

  async function load(silent=false){
    if(!silent) setLoading(true);
    const {data:msgs}=await supabase.from("system_messages")
      .select("*").not("recipient_id","is",null).order("created_at",{ascending:true});
    const {data:replies}=await supabase.from("user_replies")
      .select("*").order("created_at",{ascending:true});

    if((!msgs||msgs.length===0)&&(!replies||replies.length===0)){
      setLoading(false);
      return;
    }

    // Get all unique user_ids to fetch current names + emails from profiles
    const allUserIds=new Set([
      ...(msgs||[]).map(m=>m.recipient_id).filter(Boolean),
      ...(replies||[]).map(r=>r.user_id).filter(Boolean),
    ]);

    const profileMap={};
    if(allUserIds.size>0){
      const {data:profiles}=await supabase.from("profiles")
        .select("id,player_name,email")
        .in("id",[...allUserIds]);
      (profiles||[]).forEach(p=>{ profileMap[p.id]={name:p.player_name,email:p.email}; });
    }

    const map={};
    (msgs||[]).forEach(m=>{
      const uid=m.recipient_id;
      if(!uid) return;
      if(!map[uid]) map[uid]={userId:uid,playerName:null,email:null,items:[],lastAt:null};
      const p=profileMap[uid];
      if(p){ map[uid].playerName=p.name; map[uid].email=p.email; }
      map[uid].items.push({type:"admin",text:m.message,date:m.created_at});
      map[uid].lastAt=m.created_at;
    });
    (replies||[]).forEach(r=>{
      const uid=r.user_id;
      if(!uid) return;
      if(!map[uid]) map[uid]={userId:uid,playerName:null,email:null,items:[],lastAt:null};
      const p=profileMap[uid];
      if(p){ map[uid].playerName=p.name; map[uid].email=p.email; }
      if(!map[uid].playerName) map[uid].playerName=r.player_name||r.email||"Unknown";
      if(!map[uid].email) map[uid].email=r.email||null;
      map[uid].items.push({type:"user",text:r.reply,date:r.created_at});
      if(!map[uid].lastAt||r.created_at>map[uid].lastAt) map[uid].lastAt=r.created_at;
    });

    const sorted=Object.values(map)
      .map(t=>({...t, items:t.items.sort((a,b)=>new Date(a.date)-new Date(b.date))}))
      .sort((a,b)=>b.lastAt?.localeCompare(a.lastAt||"")||0);
    setThreads(sorted);
    if(!silent) setLoading(false);
  }

  async function sendAdminReply(userId){
    if(!replyText.trim()) return;
    setReplySending(true);
    await supabase.from("system_messages").insert({
      recipient_id:userId,
      sent_by:"admin",
      message:replyText.trim(),
      status:"unread",
      created_at:new Date().toISOString(),
    });
    setReplyText("");
    setReplySending(false);
    load(true); // silent refresh — no loading flicker
  }

  if(loading) return <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",padding:12}}>Loading conversations...</div>;
  if(threads.length===0) return(
    <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",padding:"20px 0",textAlign:"center"}}>
      No conversations yet
    </div>
  );

  return(
    <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"18px"}}>
      <div className="sys-label" style={{marginBottom:14}}>
        CONVERSATIONS ({threads.length})
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {threads.map((t,i)=>{
          const isOpen=openThread===t.userId;
          const lastItem=t.items[t.items.length-1];
          const unread=t.items.filter(x=>x.type==="user").length;
          return(
            <div key={i} style={{borderRadius:10,overflow:"hidden",
              border:`1px solid ${isOpen?SYS_BORDER:"rgba(255,255,255,0.05)"}`}}>
              {/* Thread header — always visible */}
              <div onClick={()=>{AudioEngine.click();setOpenThread(isOpen?null:t.userId);}}
                style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",
                  cursor:"pointer",background:isOpen?"rgba(56,189,248,0.06)":"rgba(255,255,255,0.01)"}}>
                <div style={{width:34,height:34,borderRadius:8,background:SYS_DIM,
                  border:`1px solid ${SYS_BORDER}`,display:"flex",alignItems:"center",
                  justifyContent:"center",fontFamily:"'Share Tech Mono',monospace",
                  fontSize:13,color:SYS,fontWeight:700,flexShrink:0}}>
                  {(t.playerName||"?")[0].toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,
                    color:"#fff",fontWeight:700,marginBottom:2}}>
                    {t.playerName||"Unknown Player"}
                  </div>
                  <div style={{fontFamily:"monospace",fontSize:9,color:"#38bdf8",
                    marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {t.email||"no email"}
                  </div>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {lastItem?.type==="user"
                      ?<span style={{color:"#38bdf8"}}>↩ {lastItem.text}</span>
                      :lastItem?.text}
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",marginBottom:4}}>
                    {new Date(t.lastAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                  </div>
                  <div style={{fontFamily:"monospace",fontSize:9,color:SYS}}>
                    {t.items.length} msg{t.items.length>1?"s":""}
                    {unread>0&&<span style={{color:"#4ade80",marginLeft:4}}>· {unread} repl{unread>1?"ies":"y"}</span>}
                  </div>
                </div>
                <span style={{color:"#64748b",fontSize:10,marginLeft:4}}>{isOpen?"▲":"▼"}</span>
              </div>

              {/* Thread messages — expanded */}
              {isOpen&&(
                <div style={{background:"rgba(0,0,0,0.2)",
                  borderTop:"1px solid rgba(255,255,255,0.04)"}}>
                  {/* Scrollable messages — fixed height */}
                  <div style={{height:240,overflowY:"auto",
                    padding:"10px 14px",display:"flex",flexDirection:"column",gap:8}}>
                    {t.items.map((item,j)=>(
                      <div key={j} style={{display:"flex",
                        justifyContent:item.type==="admin"?"flex-start":"flex-end"}}>
                        <div style={{maxWidth:"85%",padding:"9px 12px",borderRadius:10,
                          background:item.type==="admin"?"rgba(56,189,248,0.08)":"rgba(74,222,128,0.08)",
                          border:`1px solid ${item.type==="admin"?"rgba(56,189,248,0.15)":"rgba(74,222,128,0.15)"}`}}>
                          <div style={{fontFamily:"monospace",fontSize:9,
                            color:item.type==="admin"?"#38bdf8":"#4ade80",
                            marginBottom:4,letterSpacing:1}}>
                            {item.type==="admin"?"YOU (ADMIN)":t.playerName||"PLAYER"}
                          </div>
                          <div style={{fontFamily:"monospace",fontSize:11,
                            color:item.type==="admin"?"#aaa":"#ddeeff",
                            lineHeight:1.6,whiteSpace:"pre-wrap"}}>
                            {item.text.replace(/^\[CONTACT US\]\s*/,"")}
                          </div>
                          <div style={{fontFamily:"monospace",fontSize:8,color:"#64748b",marginTop:4,textAlign:"right"}}>
                            {new Date(item.date).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Admin reply input */}
                  <div style={{padding:"10px 14px 14px",borderTop:"1px solid rgba(255,255,255,0.04)",
                    display:"flex",gap:8,alignItems:"flex-end"}}>
                    <textarea
                      value={openThread===t.userId?replyText:""}
                      onChange={e=>setReplyText(e.target.value)}
                      placeholder="Reply to player..."
                      rows={2}
                      style={{flex:1,background:"rgba(56,189,248,0.05)",
                        border:`1px solid ${SYS_BORDER}`,borderRadius:8,
                        padding:"8px 10px",color:"#fff",fontSize:11,outline:"none",
                        fontFamily:"monospace",resize:"none",boxSizing:"border-box",lineHeight:1.5}}/>
                    <button onClick={()=>sendAdminReply(t.userId)}
                      disabled={replySending||!replyText.trim()}
                      style={{padding:"10px 14px",background:SYS_DIM,
                        border:`1px solid ${SYS_BORDER}`,borderRadius:8,
                        color:SYS,fontFamily:"'Share Tech Mono',monospace",
                        fontSize:10,cursor:"pointer",fontWeight:700,
                        opacity:replyText.trim()?1:0.4,whiteSpace:"nowrap"}}>
                      {replySending?"...":"SEND ↑"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Admin Dashboard ──────────────────────────────────────────────────────────
export default function AdminDashboard({onClose}){
  const [stats,setStats]=useState(null);
  const [loading,setLoading]=useState(true);
  const [activeTab,setActiveTab]=useState("overview");
  const [adminList,setAdminList]=useState([]);
  const [newAdminEmail,setNewAdminEmail]=useState("");
  const [adminMsg,setAdminMsg]=useState("");

  const [premiumRequests,setPremiumRequests]=useState([]);
  const [premiumNames,setPremiumNames]=useState([]);
  const [newPremiumName,setNewPremiumName]=useState("");
  const [premiumMsg,setPremiumMsg]=useState("");
  const [namesSubTab,setNamesSubTab]=useState("pending"); // "pending"|"resolved"|"search"|"manual"
  const [nameSearch,setNameSearch]=useState("");
  const [nameSearchResults,setNameSearchResults]=useState([]); // {name,granted_to,in_db}
  const [nameSearchLoading,setNameSearchLoading]=useState(false);
  // Messages
  const [allUsers,setAllUsers]=useState([]);
  const [userSearch,setUserSearch]=useState("");
  const [selectedUsers,setSelectedUsers]=useState([]); // user ids
  const [msgMode,setMsgMode]=useState("select"); // "select"|"global"
  const [msgText,setMsgText]=useState("");
  const [msgSendStatus,setMsgSendStatus]=useState("");
  // Users tab — revoke premium
  const [selectedUser,setSelectedUser]=useState(null);
  const [userSortBy,setUserSortBy]=useState("date"); // "date"|"name"
  const [userSortDir,setUserSortDir]=useState("desc"); // "asc"|"desc"
  const [revokeStep,setRevokeStep]=useState(0); // 0=none,1=first confirm,2=second confirm
  const [revokeKeepName,setRevokeKeepName]=useState(true);
  const [revokeMsg,setRevokeMsg]=useState("");
  // Approve confirm
  const [approveConfirm,setApproveConfirm]=useState(null); // req pending confirm
  const [sentMessages,setSentMessages]=useState([]);
  // Quick-message directly from a premium name request
  const [messagingReq,setMessagingReq]=useState(null); // request id with composer open
  const [quickMsgText,setQuickMsgText]=useState("");
  const [quickMsgStatus,setQuickMsgStatus]=useState("");

  useEffect(()=>{fetchStats(); fetchAdmins(); fetchPremiumRequests(); fetchPremiumNames(); fetchAllUsers(); fetchSentMessages();},[]);

  async function fetchAllUsers(){
    const {data}=await supabase.from("profiles").select("id,player_name,email,created_at").order("player_name");
    if(!data) return;
    const {data:premGrants}=await supabase.from("premium_usernames").select("granted_to,name").not("granted_to","is",null);
    const premMap={};
    (premGrants||[]).forEach(p=>{ premMap[p.granted_to]=p.name; });
    setAllUsers(data.map(u=>({...u, premiumName:premMap[u.id]||null})));
  }
  async function fetchSentMessages(){
    const {data}=await supabase.from("system_messages").select("*").order("created_at",{ascending:false}).limit(50);
    if(data) setSentMessages(data);
  }
  async function sendMessage(){
    if(!msgText.trim()){setMsgSendStatus("Enter a message.");return;}
    if(msgMode==="select"&&selectedUsers.length===0){setMsgSendStatus("Select at least one user.");return;}
    setMsgSendStatus("Sending...");
    try{
      if(msgMode==="global"){
        // One row with null recipient = global
        const {error}=await supabase.from("system_messages").insert({
          recipient_id:null,message:msgText.trim(),status:"unread",sent_by:"admin"
        });
        if(error) throw error;
        setMsgSendStatus("✓ Global announcement sent to all users.");
      } else {
        // One row per selected user
        const rows=selectedUsers.map(uid=>({
          recipient_id:uid,message:msgText.trim(),status:"unread",sent_by:"admin"
        }));
        const {error}=await supabase.from("system_messages").insert(rows);
        if(error) throw error;
        setMsgSendStatus("✓ Message sent to "+selectedUsers.length+" player"+(selectedUsers.length>1?"s":"")+".");
      }
      setMsgText("");setSelectedUsers([]);
      fetchSentMessages();
    }catch(e){setMsgSendStatus("✗ "+e.message);}
  }

  async function searchPremiumNames(q){
    const v=sanitizeName(q);
    if(!v||v.length<2){setNameSearchResults([]);return;}
    setNameSearchLoading(true);
    const {data}=await supabase.from("premium_usernames")
      .select("name,granted_to")
      .ilike("name",`%${v}%`)
      .order("name").limit(30);
    const results=(data||[]).map(r=>({...r,in_db:true}));
    // If exact match not found, show add option
    const exactMatch=results.find(r=>r.name===v);
    if(!exactMatch) results.unshift({name:v,granted_to:null,in_db:false});
    setNameSearchResults(results);
    setNameSearchLoading(false);
  }

  async function fetchPremiumRequests(){
    const {data}=await supabase.from("username_requests")
      .select("*").order("created_at",{ascending:false});
    if(data) setPremiumRequests(data);
  }
  async function fetchPremiumNames(){
    const {data}=await supabase.from("premium_usernames")
      .select("*").order("name");
    if(data) setPremiumNames(data);
  }
  async function addPremiumName(){
    const n=sanitizeName(newPremiumName.trim());
    if(!n||n.length<3){setPremiumMsg("Enter a valid name (min 3 chars).");return;}
    const {error}=await supabase.from("premium_usernames").insert({name:n,granted_to:null,granted_by:"manual"});
    if(error){setPremiumMsg("✗ "+error.message);return;}
    setPremiumMsg("✓ Reserved: "+n);
    setNewPremiumName("");
    fetchPremiumNames();
  }
  async function removePremiumName(name){
    await supabase.from("premium_usernames").delete().eq("name",name);
    fetchPremiumNames();
  }
  async function approveRequest(req){
    const {error:e1}=await supabase.from("premium_usernames")
      .upsert({name:req.requested_name,granted_to:req.user_id,granted_by:"manual",granted_at:new Date().toISOString()});
    if(e1){setPremiumMsg("✗ "+e1.message);setApproveConfirm(null);return;}
    await supabase.from("profiles").update({player_name:req.requested_name}).eq("id",req.user_id);
    await supabase.from("username_requests")
      .update({status:"approved",resolved_at:new Date().toISOString()})
      .eq("id",req.id);
    setPremiumMsg("✓ Approved: "+req.requested_name+" — player name updated.");
    setApproveConfirm(null);
    fetchPremiumRequests(); fetchPremiumNames(); fetchAllUsers();
  }
  async function denyRequest(req){
    await supabase.from("username_requests")
      .update({status:"denied",resolved_at:new Date().toISOString()})
      .eq("id",req.id);
    setPremiumMsg("Request denied.");
    fetchPremiumRequests();
  }
  async function revokePremium(u, keepName){
    await supabase.from("premium_usernames")
      .update({granted_to:null,granted_at:null})
      .eq("granted_to",u.id);
    if(!keepName){
      const fallback=(u.email||"").split("@")[0].toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,25)||"PLAYER";
      await supabase.from("profiles").update({player_name:fallback}).eq("id",u.id);
    }
    setRevokeMsg("✓ Premium revoked"+(keepName?"":" and name reset")+" for "+u.player_name);
    setRevokeStep(0); setSelectedUser(null);
    fetchAllUsers(); fetchPremiumNames();
  }

  async function sendQuickMessage(req){
    if(!quickMsgText.trim()){setQuickMsgStatus("Enter a message.");return;}
    try{
      const {error}=await supabase.from("system_messages").insert({
        recipient_id:req.user_id,message:quickMsgText.trim(),status:"unread",sent_by:"admin"
      });
      if(error)throw error;
      setQuickMsgStatus("✓ Sent.");
      fetchSentMessages();
      setTimeout(()=>{setMessagingReq(null);setQuickMsgText("");setQuickMsgStatus("");},900);
    }catch(e){setQuickMsgStatus("✗ "+e.message);}
  }
  async function fetchAdmins(){
    const {data}=await supabase.from("admins").select("*").order("created_at",{ascending:false});
    if(data) setAdminList(data);
  }

  async function addAdmin(){
    const email=newAdminEmail.trim().toLowerCase();
    if(!email) return;
    setAdminMsg("");
    const {error}=await supabase.from("admins").insert({email});
    if(error){
      setAdminMsg(error.code==="23505"?"Already an admin.":"Error: "+error.message);
    } else {
      setNewAdminEmail("");
      setAdminMsg("✓ Admin access granted.");
      fetchAdmins();
    }
  }

  async function removeAdmin(email){
    await supabase.from("admins").delete().eq("email",email);
    setAdminMsg("Removed "+email);
    fetchAdmins();
  }

  async function fetchStats(){
    setLoading(true);
    try{
      // Single source of truth — profiles for users, tasks for activity
      const {data:users}=await supabase.from("profiles").select("*");
      const {data:allTasks}=await supabase.from("tasks").select("*");

      const now=new Date();
      const weekAgo=new Date(now-7*86400000);
      const monthAgo=new Date(now-30*86400000);

      // Parse all logs from tasks
      const allLogs=[];
      const catCounts={};
      const metricCounts={};
      const userXP={};

      (allTasks||[]).forEach(row=>{
        const t=row.data;
        const uid=row.user_id;
        if(!t)return;
        catCounts[t.cat]=(catCounts[t.cat]||0)+1;
        metricCounts[t.metric]=(metricCounts[t.metric]||0)+1;
        (t.logs||[]).forEach(l=>{
          allLogs.push({...l,user_id:uid,taskTitle:t.title,cat:t.cat});
        });
        const xpPerLog=t.xp&&t.goals?.weekly?.amount?t.xp/parseFloat(t.goals.weekly.amount):t.xp||10;
        userXP[uid]=(userXP[uid]||0)+(t.logs||[]).length*xpPerLog;
      });

      // Active users — based on log dates
      const activeThisWeek=new Set(allLogs.filter(l=>new Date(l.date)>=weekAgo).map(l=>l.user_id)).size;
      const activeThisMonth=new Set(allLogs.filter(l=>new Date(l.date)>=monthAgo).map(l=>l.user_id)).size;

      // Logs per day
      const logsByDay={};
      allLogs.forEach(l=>{
        const day=new Date(l.date).toLocaleDateString("en-US",{month:"short",day:"numeric"});
        logsByDay[day]=(logsByDay[day]||0)+1;
      });

      // Top users by XP
      const topUsers=(users||[]).map(u=>({
        name:u.player_name||"Unknown",
        email:u.email||"",
        xp:Math.floor(userXP[u.id]||0),
        logs:(allTasks||[]).filter(t=>t.user_id===u.id).reduce((s,t)=>s+(t.data?.logs||[]).length,0)
      })).sort((a,b)=>b.xp-a.xp).slice(0,10);

      // Signups over time — use profiles.created_at (same count as totalUsers)
      const signupsByDay={};
      (users||[]).forEach(u=>{
        if(!u.created_at)return;
        const day=new Date(u.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric"});
        signupsByDay[day]=(signupsByDay[day]||0)+1;
      });

      const totalUsers=(users||[]).length;
      setStats({
        totalUsers,
        totalLogs:allLogs.length,
        activeThisWeek,
        activeThisMonth,
        avgLogsPerUser:totalUsers>0?(allLogs.length/totalUsers).toFixed(1):0,
        catCounts,
        metricCounts,
        topUsers,
        logsByDay,
        signupsByDay,
        recentLogs:allLogs.sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0,20),
      });
    }catch(e){console.error("Admin stats error:",e);}
    setLoading(false);
  }

  const tabStyle=(t)=>({
    padding:"8px 16px",fontFamily:"monospace",fontSize:10,letterSpacing:2,
    cursor:"pointer",borderRadius:6,border:"none",
    background:activeTab===t?SYS:"rgba(255,255,255,0.04)",
    color:activeTab===t?"#000":"#94a3b8",
  });

  const statCard=(label,value,sub)=>(
    <div style={{background:"rgba(56,189,248,0.06)",border:`1px solid ${SYS_BORDER}`,
      borderRadius:12,padding:"16px 18px",flex:1,minWidth:120}}>
      <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:2,marginBottom:6}}>{label}</div>
      <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:28,fontWeight:800,color:SYS}}>{value}</div>
      {sub&&<div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:4}}>{sub}</div>}
    </div>
  );

  const barChart=(data,colorFn)=>{
    const entries=Object.entries(data||{}).sort((a,b)=>b[1]-a[1]).slice(0,8);
    const max=Math.max(...entries.map(e=>e[1]),1);
    return(
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {entries.map(([k,v])=>(
          <div key={k} style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{fontFamily:"monospace",fontSize:10,color:"#aaa",minWidth:80,textAlign:"right"}}>{k}</div>
            <div style={{flex:1,height:20,background:"rgba(255,255,255,0.03)",borderRadius:4,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${(v/max)*100}%`,
                background:colorFn?colorFn(k):"linear-gradient(90deg,#38bdf8,#818cf8)",
                borderRadius:4,transition:"width 0.5s ease",
                display:"flex",alignItems:"center",paddingLeft:8}}>
                <span style={{fontFamily:"monospace",fontSize:9,color:"#000",fontWeight:700}}>{v}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return(
    <div style={{position:"fixed",inset:0,background:"#000",zIndex:9000,overflowY:"auto",
      fontFamily:"'Outfit',sans-serif"}}>
      {/* Header */}
      <div style={{background:"#030912",borderBottom:`1px solid ${SYS_BORDER}`,
        padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",
        position:"sticky",top:0,zIndex:10}}>
        <div>
          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:SYS,letterSpacing:2,whiteSpace:"nowrap"}}>
            [ ADMIN DASHBOARD ]
          </div>
          <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:2,marginTop:2}}>
            A.R.I.S.E RESEARCH PANEL
          </div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <button onClick={()=>{AudioEngine.click();fetchStats()}} style={{background:SYS_DIM,border:`1px solid ${SYS_BORDER}`,
            color:SYS,borderRadius:8,padding:"6px 12px",fontFamily:"monospace",fontSize:10,cursor:"pointer"}}>
            ↻ REFRESH
          </button>
          <button onClick={()=>{AudioEngine.close();onClose();}} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",
            color:"#667",borderRadius:8,padding:"6px 12px",fontFamily:"monospace",fontSize:10,cursor:"pointer"}}>
            ✕ CLOSE
          </button>
        </div>
      </div>

      {loading?(
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"60vh"}}>
          <div style={{fontFamily:"monospace",fontSize:12,color:SYS,letterSpacing:3}}>LOADING DATA...</div>
        </div>
      ):(
        <div style={{padding:"20px",maxWidth:800,margin:"0 auto"}}>
          {/* Tabs */}
          <div style={{display:"flex",gap:8,marginBottom:24,flexWrap:"wrap"}}>
            {["overview","categories","users","activity","names","messages","admins"].map(t=>(
              <button key={t} style={tabStyle(t)} onClick={()=>setActiveTab(t)}>
                {t.toUpperCase()}
              </button>
            ))}
          </div>

          {activeTab==="overview"&&(
            <>
              {/* Stat cards */}
              <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:24}}>
                {statCard("TOTAL USERS",stats.totalUsers)}
                {statCard("TOTAL LOGS",stats.totalLogs)}
                {statCard("ACTIVE THIS WEEK",stats.activeThisWeek,"unique users")}
                {statCard("ACTIVE THIS MONTH",stats.activeThisMonth,"unique users")}
                {statCard("AVG LOGS/USER",stats.avgLogsPerUser)}
              </div>

              {/* Signups over time */}
              <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:12,padding:"18px",marginBottom:16}}>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:14}}>
                  SIGNUPS OVER TIME
                </div>
                {barChart(stats.signupsByDay)}
              </div>

              {/* Logs over time */}
              <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:12,padding:"18px"}}>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:14}}>
                  LOGS OVER TIME
                </div>
                {barChart(stats.logsByDay)}
              </div>
            </>
          )}

          {activeTab==="categories"&&(
            <>
              <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:12,padding:"18px",marginBottom:16}}>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:14}}>
                  TASKS BY CATEGORY
                </div>
                {barChart(stats.catCounts,(k)=>CAT[k]?.color||SYS)}
              </div>
              <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:12,padding:"18px"}}>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:14}}>
                  TASKS BY METRIC TYPE
                </div>
                {barChart(stats.metricCounts)}
              </div>
            </>
          )}

          {activeTab==="users"&&(
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {/* Revoke result message */}
              {revokeMsg&&(
                <div style={{padding:"10px 14px",background:"rgba(74,222,128,0.08)",
                  border:"1px solid rgba(74,222,128,0.25)",borderRadius:10,
                  fontFamily:"monospace",fontSize:11,color:"#4ade80",letterSpacing:1}}>
                  {revokeMsg}
                  <button onClick={()=>setRevokeMsg("")}
                    style={{float:"right",background:"none",border:"none",color:"#4ade80",cursor:"pointer"}}>✕</button>
                </div>
              )}

              {/* User detail panel — revoke */}
              {selectedUser&&(
                <div style={{background:"rgba(0,0,0,0.4)",border:`1px solid ${selectedUser.premiumName?"rgba(251,191,36,0.3)":SYS_BORDER}`,
                  borderRadius:12,padding:"16px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                    <div>
                      <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:14,
                        color:selectedUser.premiumName?"#fbbf24":"#fff",fontWeight:800,marginBottom:4}}>
                        {selectedUser.premiumName&&"✦ "}{selectedUser.player_name}
                      </div>
                      <div style={{fontFamily:"monospace",fontSize:10,color:"#38bdf8"}}>{selectedUser.email}</div>
                      {selectedUser.premiumName&&(
                        <div style={{fontFamily:"monospace",fontSize:9,color:"#f59e0b",marginTop:4}}>
                          Premium name: {selectedUser.premiumName}
                        </div>
                      )}
                    </div>
                    <button onClick={()=>{setSelectedUser(null);setRevokeStep(0);}}
                      style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:16}}>✕</button>
                  </div>

                  {selectedUser.premiumName?(
                    revokeStep===0?(
                      <button onClick={()=>setRevokeStep(1)}
                        style={{width:"100%",padding:"10px",background:"rgba(239,68,68,0.08)",
                          border:"1px solid rgba(239,68,68,0.25)",borderRadius:8,
                          color:WARN,fontFamily:"'Share Tech Mono',monospace",fontSize:10,
                          letterSpacing:1,cursor:"pointer"}}>
                        REVOKE PREMIUM
                      </button>
                    ):revokeStep===1?(
                      <div>
                        <div style={{fontFamily:"monospace",fontSize:11,color:"#94a3b8",marginBottom:10}}>
                          What should happen to their name after revoking?
                        </div>
                        <div style={{display:"flex",gap:8,marginBottom:10}}>
                          <button onClick={()=>setRevokeKeepName(true)}
                            style={{flex:1,padding:"9px",borderRadius:8,cursor:"pointer",fontFamily:"monospace",fontSize:10,
                              background:revokeKeepName?"rgba(56,189,248,0.12)":"rgba(255,255,255,0.03)",
                              border:`1px solid ${revokeKeepName?SYS_BORDER:"rgba(255,255,255,0.08)"}`,
                              color:revokeKeepName?SYS:"#64748b"}}>
                            Keep name (remove gold only)
                          </button>
                          <button onClick={()=>setRevokeKeepName(false)}
                            style={{flex:1,padding:"9px",borderRadius:8,cursor:"pointer",fontFamily:"monospace",fontSize:10,
                              background:!revokeKeepName?"rgba(239,68,68,0.1)":"rgba(255,255,255,0.03)",
                              border:`1px solid ${!revokeKeepName?"rgba(239,68,68,0.3)":"rgba(255,255,255,0.08)"}`,
                              color:!revokeKeepName?WARN:"#64748b"}}>
                            Reset name too
                          </button>
                        </div>
                        <button onClick={()=>setRevokeStep(2)}
                          style={{width:"100%",padding:"10px",background:"rgba(239,68,68,0.1)",
                            border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,
                            color:WARN,fontFamily:"'Share Tech Mono',monospace",fontSize:10,
                            letterSpacing:1,cursor:"pointer",fontWeight:700}}>
                          CONFIRM — REVOKE PREMIUM
                        </button>
                      </div>
                    ):(
                      <div>
                        <div style={{padding:"10px 14px",background:"rgba(239,68,68,0.08)",
                          border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,marginBottom:10,
                          fontFamily:"monospace",fontSize:11,color:WARN}}>
                          ⚠️ Final confirmation — this cannot be undone.{!revokeKeepName&&" Player name will be reset."}
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={()=>setRevokeStep(0)}
                            style={{flex:1,padding:"9px",background:"rgba(255,255,255,0.03)",
                              border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,
                              color:"#64748b",fontFamily:"monospace",fontSize:10,cursor:"pointer"}}>
                            CANCEL
                          </button>
                          <button onClick={()=>revokePremium(selectedUser,revokeKeepName)}
                            style={{flex:2,padding:"9px",background:"rgba(239,68,68,0.15)",
                              border:"1px solid rgba(239,68,68,0.4)",borderRadius:8,
                              color:WARN,fontFamily:"'Share Tech Mono',monospace",fontSize:10,
                              letterSpacing:1,cursor:"pointer",fontWeight:700}}>
                            ✓ YES, REVOKE
                          </button>
                        </div>
                      </div>
                    )
                  ):(
                    <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b"}}>
                      This user does not have a premium name.
                    </div>
                  )}
                </div>
              )}

              {/* Search + all users list */}
              <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:12,padding:"16px"}}>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:12}}>
                  ALL USERS ({allUsers.length})
                </div>
                <input value={userSearch} onChange={e=>setUserSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  style={{width:"100%",background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
                    borderRadius:8,padding:"9px 12px",color:"#fff",fontSize:11,outline:"none",
                    fontFamily:"monospace",marginBottom:10,boxSizing:"border-box"}}/>

                <div style={{display:"flex",gap:6,marginBottom:10,alignItems:"center"}}>
                  <span style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:1,marginRight:2}}>SORT</span>
                  <button onClick={()=>{AudioEngine.click();setUserSortBy("date");}}
                    style={{padding:"5px 10px",borderRadius:6,cursor:"pointer",fontFamily:"monospace",fontSize:9,letterSpacing:1,
                      background:userSortBy==="date"?SYS:"rgba(255,255,255,0.03)",
                      border:`1px solid ${userSortBy==="date"?SYS:"rgba(255,255,255,0.08)"}`,
                      color:userSortBy==="date"?"#000":"#64748b",fontWeight:userSortBy==="date"?700:400}}>
                    DATE
                  </button>
                  <button onClick={()=>{AudioEngine.click();setUserSortBy("name");}}
                    style={{padding:"5px 10px",borderRadius:6,cursor:"pointer",fontFamily:"monospace",fontSize:9,letterSpacing:1,
                      background:userSortBy==="name"?SYS:"rgba(255,255,255,0.03)",
                      border:`1px solid ${userSortBy==="name"?SYS:"rgba(255,255,255,0.08)"}`,
                      color:userSortBy==="name"?"#000":"#64748b",fontWeight:userSortBy==="name"?700:400}}>
                    A–Z
                  </button>
                  <div style={{width:1,height:16,background:"rgba(255,255,255,0.1)",margin:"0 2px"}}/>
                  <button onClick={()=>{AudioEngine.click();setUserSortDir(userSortDir==="asc"?"desc":"asc");}}
                    style={{padding:"5px 10px",borderRadius:6,cursor:"pointer",fontFamily:"monospace",fontSize:9,letterSpacing:1,
                      background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",color:SYS}}>
                    {userSortDir==="asc"?"↑ ASC":"↓ DESC"}
                  </button>
                </div>

                <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:400,overflowY:"auto"}}>
                  {allUsers
                    .filter(u=>!userSearch||
                      u.player_name?.toLowerCase().includes(userSearch.toLowerCase())||
                      u.email?.toLowerCase().includes(userSearch.toLowerCase()))
                    .sort((a,b)=>{
                      let cmp;
                      if(userSortBy==="name"){
                        cmp=(a.player_name||"").localeCompare(b.player_name||"");
                      }else{
                        cmp=new Date(a.created_at)-new Date(b.created_at);
                      }
                      return userSortDir==="asc"?cmp:-cmp;
                    })
                    .map((u,i)=>(
                      <div key={i} onClick={()=>{AudioEngine.click();setSelectedUser(u);setRevokeStep(0);setRevokeMsg("");}}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                          cursor:"pointer",borderRadius:8,
                          background:selectedUser?.id===u.id?"rgba(56,189,248,0.08)":"rgba(255,255,255,0.01)",
                          border:`1px solid ${selectedUser?.id===u.id?SYS_BORDER:u.premiumName?"rgba(251,191,36,0.2)":"rgba(255,255,255,0.04)"}`}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,fontWeight:700,
                              color:u.premiumName?"#fbbf24":"#ddeeff",
                              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {u.premiumName?"✦ ":""}{u.player_name}
                            </span>
                            {u.premiumName&&(
                              <span style={{fontFamily:"monospace",fontSize:8,color:"#f59e0b",
                                background:"rgba(251,191,36,0.1)",padding:"1px 5px",
                                borderRadius:3,flexShrink:0,border:"1px solid rgba(251,191,36,0.2)"}}>
                                PREMIUM
                              </span>
                            )}
                          </div>
                          <div style={{fontFamily:"monospace",fontSize:9,color:"#38bdf8",marginTop:2,
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {u.email}
                          </div>
                        </div>
                        <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",flexShrink:0}}>
                          {new Date(u.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"2-digit"})}
                        </div>
                      </div>
                    ))}
                  {allUsers.length===0&&(
                    <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",textAlign:"center",padding:20}}>
                      No users yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab==="activity"&&(
            <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
              borderRadius:12,padding:"18px"}}>
              <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:14}}>
                RECENT LOGS (LAST 20)
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {stats.recentLogs.map((l,i)=>{
                  const cfg=CAT[l.cat];
                  return(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:10,
                      padding:"8px 12px",background:"rgba(255,255,255,0.02)",
                      border:"1px solid rgba(255,255,255,0.04)",borderRadius:8}}>
                      <span style={{fontSize:12}}>{cfg?.icon||"📋"}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,color:"#ddeeff",fontWeight:600}}>{l.taskTitle}</div>
                        <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",marginTop:2}}>
                          {new Date(l.date).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                        </div>
                      </div>
                      <div style={{fontFamily:"monospace",fontSize:9,
                        color:CAT[l.cat]?.color||"#64748b",
                        background:`${CAT[l.cat]?.color||"#445"}22`,
                        padding:"2px 8px",borderRadius:4}}>{l.cat}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab==="messages"&&(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>

              {/* Mode toggle */}
              <div style={{display:"flex",gap:8}}>
                {["select","global"].map(m=>(
                  <button key={m} onClick={()=>{AudioEngine.click();setMsgMode(m);setSelectedUsers([]);setMsgSendStatus("");}}
                    style={{flex:1,padding:"10px",fontFamily:"monospace",fontSize:10,letterSpacing:2,cursor:"pointer",
                      borderRadius:10,fontWeight:700,
                      background:msgMode===m?SYS:"rgba(255,255,255,0.03)",
                      border:msgMode===m?"none":`1px solid rgba(255,255,255,0.06)`,
                      color:msgMode===m?"#000":"#94a3b8"}}>
                    {m==="select"?"SELECT USERS":"🌐 GLOBAL"}
                  </button>
                ))}
              </div>

              {/* Compose box */}
              <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"18px"}}>
                <div className="sys-label" style={{marginBottom:12}}>
                  {msgMode==="global"?"GLOBAL ANNOUNCEMENT — ALL PLAYERS":"SEND MESSAGE TO SELECTED PLAYERS"}
                </div>

                {msgMode==="select"&&(
                  <div style={{marginBottom:14}}>
                    {/* Search */}
                    <input value={userSearch} onChange={e=>setUserSearch(e.target.value)}
                      placeholder="Search player name..."
                      style={{width:"100%",background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
                        borderRadius:10,padding:"10px 14px",color:"#fff",fontSize:12,outline:"none",
                        fontFamily:"monospace",marginBottom:10,boxSizing:"border-box"}}/>
                    {/* User list */}
                    <div style={{maxHeight:180,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
                      {allUsers
                        .filter(u=>!userSearch||u.player_name?.toLowerCase().includes(userSearch.toLowerCase()))
                        .map((u,i)=>{
                          const sel=selectedUsers.includes(u.id);
                          return(
                            <div key={i} onClick={()=>{AudioEngine.click();setSelectedUsers(prev=>sel?prev.filter(x=>x!==u.id):[...prev,u.id]);}}
                              style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",
                                cursor:"pointer",borderRadius:8,
                                background:sel?"rgba(56,189,248,0.08)":"rgba(255,255,255,0.01)",
                                border:`1px solid ${sel?SYS_BORDER:"rgba(255,255,255,0.04)"}`}}>
                              <div style={{width:16,height:16,borderRadius:4,flexShrink:0,
                                background:sel?SYS:"rgba(255,255,255,0.04)",
                                border:`1px solid ${sel?SYS:"rgba(255,255,255,0.1)"}`,
                                display:"flex",alignItems:"center",justifyContent:"center"}}>
                                {sel&&<span style={{fontSize:10,color:"#000",fontWeight:900}}>✓</span>}
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,
                                  color:sel?"#fff":"#667"}}>{u.player_name||"Unknown"}</span>
                                {u.email&&<div style={{fontFamily:"monospace",fontSize:9,
                                  color:"#38bdf8",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  {u.email}
                                </div>}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                    {selectedUsers.length>0&&(
                      <div style={{fontFamily:"monospace",fontSize:10,color:SYS,marginTop:8,letterSpacing:1}}>
                        {selectedUsers.length} player{selectedUsers.length>1?"s":""} selected
                        <button onClick={()=>setSelectedUsers([])}
                          style={{marginLeft:10,background:"none",border:"none",color:"#64748b",
                            fontFamily:"monospace",fontSize:9,cursor:"pointer",letterSpacing:1}}>
                          CLEAR
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {msgMode==="global"&&(
                  <div style={{padding:"10px 14px",background:"rgba(245,158,11,0.06)",
                    border:"1px solid rgba(245,158,11,0.2)",borderRadius:10,marginBottom:14,
                    fontFamily:"monospace",fontSize:10,color:"#f59e0b",lineHeight:1.6}}>
                    ⚡ This message will appear for <strong>all players</strong> when they open the app.
                  </div>
                )}

                <textarea value={msgText} onChange={e=>setMsgText(e.target.value)}
                  placeholder="Type your message..."
                  rows={4}
                  style={{width:"100%",background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
                    borderRadius:10,padding:"12px 14px",color:"#fff",fontSize:13,outline:"none",
                    fontFamily:"monospace",resize:"vertical",boxSizing:"border-box",
                    lineHeight:1.6}}/>

                {msgSendStatus&&(
                  <div style={{fontFamily:"monospace",fontSize:11,marginTop:8,
                    color:msgSendStatus.startsWith("✓")?"#4ade80":msgSendStatus.startsWith("✗")?WARN:"#94a3b8"}}>
                    {msgSendStatus}
                  </div>
                )}

                <button onClick={()=>{AudioEngine.log();sendMessage();}}
                  style={{width:"100%",marginTop:12,padding:"12px",
                    background:msgMode==="global"?"rgba(245,158,11,0.15)":SYS_DIM,
                    border:`1px solid ${msgMode==="global"?"rgba(245,158,11,0.35)":SYS_BORDER}`,
                    borderRadius:10,color:msgMode==="global"?"#f59e0b":SYS,
                    fontFamily:"monospace",fontSize:11,fontWeight:700,cursor:"pointer",letterSpacing:2}}>
                  {msgMode==="global"?"[ 🌐 SEND TO ALL ]":"[ SEND MESSAGE ]"}
                </button>
              </div>

              {/* User replies section */}
              <UserRepliesPanel/>

              {/* Sent messages log */}
              {sentMessages.length>0&&(
                <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"18px"}}>
                  <div className="sys-label" style={{marginBottom:14}}>SENT MESSAGES</div>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {sentMessages.map((m,i)=>(
                      <div key={i} style={{padding:"10px 14px",background:"rgba(255,255,255,0.01)",
                        border:"1px solid rgba(255,255,255,0.04)",borderRadius:8}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                          <span style={{fontFamily:"monospace",fontSize:9,color:"#64748b"}}>
                            {new Date(m.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"2-digit",minute:"2-digit"})}
                          </span>
                          <span style={{fontFamily:"monospace",fontSize:9,letterSpacing:1,
                            color:m.recipient_id===null?"#f59e0b":SYS}}>
                            {m.recipient_id===null?"GLOBAL":"DIRECT"}
                          </span>
                        </div>
                        <div style={{fontFamily:"monospace",fontSize:11,color:"#667",lineHeight:1.6,
                          whiteSpace:"pre-wrap"}}>
                          {m.message}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab==="names"&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>

              {/* Sub-tab bar */}
              <div style={{display:"flex",gap:6,background:"rgba(56,189,248,0.04)",
                borderRadius:10,padding:3,border:`1px solid ${SYS_BORDER}`}}>
                {[
                  {id:"pending",label:`PENDING (${premiumRequests.filter(r=>r.status==="pending").length})`},
                  {id:"resolved",label:`RESOLVED (${premiumRequests.filter(r=>r.status!=="pending").length})`},
                  {id:"search",label:"SEARCH LIST"},
                  {id:"manual",label:"MANUAL"},
                ].map(t=>(
                  <button key={t.id} onClick={()=>{AudioEngine.click();setNamesSubTab(t.id);}}
                    style={{flex:1,padding:"8px 4px",fontFamily:"monospace",fontSize:9,
                      letterSpacing:1,cursor:"pointer",borderRadius:8,border:"none",
                      background:namesSubTab===t.id?SYS:"transparent",
                      color:namesSubTab===t.id?"#000":"#94a3b8",fontWeight:namesSubTab===t.id?700:400,
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* ── PENDING ── */}
              {namesSubTab==="pending"&&(
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {premiumRequests.filter(r=>r.status==="pending").length===0&&(
                    <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",
                      textAlign:"center",padding:24}}>No pending requests</div>
                  )}
                  {premiumRequests.filter(r=>r.status==="pending").map((r,i)=>(
                    <div key={i} style={{padding:"14px",background:"rgba(56,189,248,0.04)",
                      border:`1px solid ${SYS_BORDER}`,borderRadius:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                        <div>
                          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:16,
                            color:"#4ade80",fontWeight:700,letterSpacing:2,marginBottom:4}}>
                            ✦ {r.requested_name}
                          </div>
                          <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                            {r.note&&r.note.startsWith("[CONTACT FORM]")?(
                              <span style={{fontFamily:"monospace",fontSize:9,
                                background:"rgba(168,85,247,0.15)",color:"#a855f7",
                                border:"1px solid rgba(168,85,247,0.3)",
                                padding:"1px 6px",borderRadius:4,letterSpacing:1}}>CONTACT</span>
                            ):!r.requested_name||r.note==="Requested via name change in app"?(
                              <span style={{fontFamily:"monospace",fontSize:9,
                                background:"rgba(56,189,248,0.12)",color:SYS,
                                border:`1px solid ${SYS_BORDER}`,
                                padding:"1px 6px",borderRadius:4,letterSpacing:1}}>STANDARD</span>
                            ):(
                              <span style={{fontFamily:"monospace",fontSize:9,
                                background:"rgba(245,158,11,0.12)",color:"#f59e0b",
                                border:"1px solid rgba(245,158,11,0.3)",
                                padding:"1px 6px",borderRadius:4,letterSpacing:1}}>CUSTOM</span>
                            )}
                          </div>
                          <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",lineHeight:1.8}}>
                            {r.email&&<div style={{color:"#38bdf8"}}>{r.email}</div>}
                            {r.current_name&&<div>Current: <span style={{color:"#aaa"}}>{r.current_name}</span></div>}
                            <div>{new Date(r.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div>
                          </div>
                          {r.note&&<div style={{fontFamily:"monospace",fontSize:10,color:"#667",
                            marginTop:6,fontStyle:"italic"}}>"{r.note}"</div>}
                        </div>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        {approveConfirm?.id===r.id?(
                          <>
                            <div style={{flex:1,display:"flex",flexDirection:"column",gap:6}}>
                              <div style={{fontFamily:"monospace",fontSize:9,color:"#f59e0b",
                                padding:"6px 8px",background:"rgba(245,158,11,0.08)",
                                border:"1px solid rgba(245,158,11,0.2)",borderRadius:6}}>
                                ⚠️ Confirm approve "{r.requested_name}"?
                              </div>
                              <div style={{display:"flex",gap:6}}>
                                <button onClick={()=>{AudioEngine.click();setApproveConfirm(null);}}
                                  style={{flex:1,padding:"7px",background:"rgba(255,255,255,0.03)",
                                    border:"1px solid rgba(255,255,255,0.08)",borderRadius:6,
                                    color:"#64748b",fontFamily:"monospace",fontSize:9,cursor:"pointer"}}>
                                  CANCEL
                                </button>
                                <button onClick={()=>{AudioEngine.click();approveRequest(r);}}
                                  style={{flex:2,padding:"7px",background:"rgba(74,222,128,0.15)",
                                    border:"1px solid rgba(74,222,128,0.35)",borderRadius:6,color:"#4ade80",
                                    fontFamily:"'Share Tech Mono',monospace",fontSize:9,cursor:"pointer",fontWeight:700}}>
                                  ✓ YES, APPROVE
                                </button>
                              </div>
                            </div>
                          </>
                        ):(
                          <>
                            <button onClick={()=>{AudioEngine.click();setApproveConfirm(r);}}
                              style={{flex:1,padding:"9px",background:"rgba(74,222,128,0.08)",
                                border:"1px solid rgba(74,222,128,0.2)",borderRadius:8,color:"#4ade80",
                                fontFamily:"monospace",fontSize:10,cursor:"pointer",letterSpacing:1,fontWeight:700}}>
                              ✓ APPROVE
                            </button>
                            <button onClick={()=>{AudioEngine.click();denyRequest(r);}}
                              style={{flex:1,padding:"9px",background:"rgba(239,68,68,0.08)",
                                border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,color:WARN,
                                fontFamily:"monospace",fontSize:10,cursor:"pointer",letterSpacing:1}}>
                              ✗ DENY
                            </button>
                            <button onClick={()=>{AudioEngine.click();
                                setMessagingReq(messagingReq===r.id?null:r.id);
                                setQuickMsgText("");setQuickMsgStatus("");}}
                              style={{flex:1,padding:"9px",background:"rgba(56,189,248,0.08)",
                                border:`1px solid ${SYS_BORDER}`,borderRadius:8,color:SYS,
                                fontFamily:"monospace",fontSize:10,cursor:"pointer",letterSpacing:1}}>
                              ✉ MESSAGE
                            </button>
                          </>
                        )}
                      </div>
                      {messagingReq===r.id&&(
                        <div style={{marginTop:10,padding:10,background:"rgba(56,189,248,0.03)",
                          border:`1px solid ${SYS_BORDER}`,borderRadius:8}}>
                          <textarea value={quickMsgText} onChange={e=>setQuickMsgText(e.target.value)}
                            placeholder={`Message to ${r.email||r.current_name||"this player"}...`}
                            rows={3} style={{width:"100%",background:"rgba(0,0,0,0.3)",
                              border:"1px solid rgba(255,255,255,0.08)",borderRadius:6,
                              color:"#fff",fontFamily:"monospace",fontSize:11,padding:8,
                              resize:"vertical",marginBottom:8,boxSizing:"border-box"}}/>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <button onClick={()=>{AudioEngine.click();sendQuickMessage(r);}}
                              style={{padding:"7px 14px",background:SYS,border:"none",borderRadius:6,
                                color:"#000",fontFamily:"monospace",fontSize:10,fontWeight:700,
                                cursor:"pointer",letterSpacing:1}}>
                              SEND
                            </button>
                            <button onClick={()=>{AudioEngine.click();setMessagingReq(null);}}
                              style={{padding:"7px 14px",background:"rgba(255,255,255,0.03)",
                                border:"1px solid rgba(255,255,255,0.08)",borderRadius:6,
                                color:"#64748b",fontFamily:"monospace",fontSize:10,cursor:"pointer"}}>
                              CANCEL
                            </button>
                            {quickMsgStatus&&<span style={{fontFamily:"monospace",fontSize:9,
                              color:quickMsgStatus.startsWith("✓")?"#4ade80":WARN}}>{quickMsgStatus}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* ── RESOLVED ── */}
              {namesSubTab==="resolved"&&(
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {premiumRequests.filter(r=>r.status!=="pending").length===0&&(
                    <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",
                      textAlign:"center",padding:24}}>No resolved requests yet</div>
                  )}
                  {premiumRequests.filter(r=>r.status!=="pending").map((r,i)=>(
                    <div key={i} style={{padding:"12px 14px",
                      background:"rgba(255,255,255,0.01)",
                      border:"1px solid rgba(255,255,255,0.04)",borderRadius:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                        <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:14,
                          color:r.status==="approved"?"#4ade80":WARN,letterSpacing:2,fontWeight:700}}>
                          ✦ {r.requested_name}
                        </span>
                        <span style={{fontFamily:"monospace",fontSize:9,letterSpacing:1,
                          padding:"3px 8px",borderRadius:4,
                          background:r.status==="approved"?"rgba(74,222,128,0.1)":"rgba(239,68,68,0.1)",
                          color:r.status==="approved"?"#4ade80":WARN}}>
                          {r.status.toUpperCase()}
                        </span>
                      </div>
                      <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b"}}>
                        {r.email&&<span style={{color:"#38bdf8",marginRight:8}}>{r.email}</span>}
                        {r.current_name&&<span>was: {r.current_name} · </span>}
                        {r.resolved_at&&new Date(r.resolved_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── SEARCH LIST ── */}
              {namesSubTab==="search"&&(
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:1}}>
                    Search across all premium names in DB. Type to find, add if missing.
                  </div>
                  <input value={nameSearch}
                    onChange={e=>{
                      const v=sanitizeName(e.target.value);
                      setNameSearch(v);
                      clearTimeout(window._nsTimer);
                      if(v.length>=2) window._nsTimer=setTimeout(()=>searchPremiumNames(v),350);
                      else setNameSearchResults([]);
                    }}
                    placeholder="SEARCH PREMIUM NAMES..."
                    style={{width:"100%",background:"rgba(56,189,248,0.05)",
                      border:`1px solid ${SYS_BORDER}`,borderRadius:10,
                      padding:"12px 14px",color:"#fff",fontSize:13,outline:"none",
                      fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,
                      boxSizing:"border-box"}}/>
                  {nameSearchLoading&&(
                    <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",
                      letterSpacing:1,textAlign:"center",padding:8}}>
                      · · · SEARCHING · · ·
                    </div>
                  )}
                  {!nameSearchLoading&&nameSearch.length>=2&&nameSearchResults.length===0&&(
                    <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",
                      textAlign:"center",padding:16}}>No results found</div>
                  )}
                  <div style={{display:"flex",flexDirection:"column",gap:6,
                    maxHeight:400,overflowY:"auto"}}>
                    {nameSearchResults.map((r,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:10,
                        padding:"10px 14px",borderRadius:10,
                        background:r.in_db?"rgba(74,222,128,0.03)":"rgba(56,189,248,0.04)",
                        border:`1px solid ${r.in_db?"rgba(74,222,128,0.12)":SYS_BORDER}`}}>
                        <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:13,
                          color:r.in_db?"#4ade80":"#38bdf8",letterSpacing:2,flex:1,fontWeight:700}}>
                          {r.in_db?"✦":"+"} {r.name}
                        </span>
                        {r.in_db&&(
                          <span style={{fontFamily:"monospace",fontSize:9,letterSpacing:1,
                            color:r.granted_to?"#4ade80":"#94a3b8"}}>
                            {r.granted_to?"OWNED":"AVAILABLE"}
                          </span>
                        )}
                        {!r.in_db&&(
                          <button onClick={()=>{
                            AudioEngine.click();
                            setNewPremiumName(r.name);
                            supabase.from("premium_usernames")
                              .insert({name:r.name,granted_to:null,granted_by:"manual"})
                              .then(()=>{
                                setNameSearchResults(prev=>prev.map(x=>x.name===r.name?{...x,in_db:true}:x));
                                setPremiumMsg("✓ Reserved: "+r.name);
                              });
                          }}
                            style={{padding:"5px 12px",background:"rgba(56,189,248,0.1)",
                              border:`1px solid ${SYS_BORDER}`,borderRadius:6,
                              color:SYS,fontFamily:"monospace",fontSize:9,
                              cursor:"pointer",letterSpacing:1,whiteSpace:"nowrap"}}>
                            + ADD
                          </button>
                        )}
                        {r.in_db&&!r.granted_to&&(
                          <button onClick={()=>{
                            AudioEngine.click();
                            supabase.from("premium_usernames").delete().eq("name",r.name)
                              .then(()=>setNameSearchResults(prev=>prev.filter(x=>x.name!==r.name)));
                          }}
                            style={{padding:"5px 10px",background:"rgba(239,68,68,0.08)",
                              border:"1px solid rgba(239,68,68,0.2)",borderRadius:6,
                              color:WARN,fontFamily:"monospace",fontSize:9,
                              cursor:"pointer",letterSpacing:1}}>
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {premiumMsg&&<div style={{fontFamily:"monospace",fontSize:11,
                    color:premiumMsg.startsWith("✓")?"#4ade80":WARN}}>{premiumMsg}</div>}
                </div>
              )}

              {/* ── MANUAL ── */}
              {namesSubTab==="manual"&&(
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  {/* Add new */}
                  <div style={{background:"rgba(255,255,255,0.02)",
                    border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"16px"}}>
                    <div className="sys-label" style={{marginBottom:12}}>RESERVE A NAME</div>
                    <div style={{display:"flex",gap:10}}>                      <input value={newPremiumName} onChange={e=>setNewPremiumName(sanitizeName(e.target.value))}
                        onKeyDown={e=>e.key==="Enter"&&addPremiumName()}
                        placeholder="USERNAME TO RESERVE..."
                        style={{flex:1,background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
                          borderRadius:10,padding:"10px 14px",color:"#fff",fontSize:13,outline:"none",
                          fontFamily:"'Share Tech Mono',monospace",letterSpacing:2}}/>
                      <button onClick={()=>{AudioEngine.click();addPremiumName();}}
                        style={{background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.3)",
                          borderRadius:10,padding:"10px 16px",color:"#4ade80",fontWeight:800,
                          fontSize:12,fontFamily:"monospace",cursor:"pointer",letterSpacing:1,whiteSpace:"nowrap"}}>
                        + RESERVE
                      </button>
                    </div>
                    {premiumMsg&&<div style={{fontFamily:"monospace",fontSize:11,marginTop:10,
                      color:premiumMsg.startsWith("✓")?"#4ade80":WARN}}>{premiumMsg}</div>}
                  </div>
                  {/* Manually reserved list — only non-system names */}
                  <div style={{background:"rgba(255,255,255,0.02)",
                    border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"16px"}}>
                    <div className="sys-label" style={{marginBottom:12}}>
                      MANUALLY RESERVED ({premiumNames.filter(n=>n.granted_by==="manual").length})
                    </div>
                    <div style={{maxHeight:360,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
                      {premiumNames.filter(n=>n.granted_by==="manual").map((n,i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",gap:10,
                          padding:"10px 14px",background:"rgba(74,222,128,0.03)",
                          border:"1px solid rgba(74,222,128,0.12)",borderRadius:8}}>
                          <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:13,
                            color:"#4ade80",letterSpacing:2,flex:1,fontWeight:700}}>✦ {n.name}</span>
                          <span style={{fontFamily:"monospace",fontSize:9,letterSpacing:1,
                            color:n.granted_to?"#4ade80":"#94a3b8"}}>
                            {n.granted_to?"OWNED":"AVAILABLE"}
                          </span>
                          {!n.granted_to&&(
                            <button onClick={()=>{AudioEngine.click();removePremiumName(n.name);}}
                              style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",
                                color:WARN,borderRadius:6,padding:"4px 10px",fontFamily:"monospace",
                                fontSize:9,cursor:"pointer",letterSpacing:1}}>
                              REMOVE
                            </button>
                          )}
                        </div>
                      ))}
                      {premiumNames.filter(n=>n.granted_by==="manual").length===0&&(
                        <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",
                          textAlign:"center",padding:16}}>No manually reserved names</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}


          {activeTab==="admins"&&(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* Add new admin */}
              <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:12,padding:"18px"}}>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:14}}>
                  GRANT ADMIN ACCESS
                </div>
                <div style={{display:"flex",gap:10}}>
                  <input value={newAdminEmail} onChange={e=>setNewAdminEmail(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&addAdmin()}
                    placeholder="Enter email address"
                    style={{flex:1,background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
                      borderRadius:10,padding:"10px 14px",color:"#fff",fontSize:13,outline:"none",
                      fontFamily:"'Outfit',sans-serif"}}/>
                  <button onClick={()=>{AudioEngine.click();addAdmin()}}
                    style={{background:SYS,border:"none",borderRadius:10,padding:"10px 18px",
                      color:"#000",fontWeight:800,fontSize:12,fontFamily:"monospace",
                      cursor:"pointer",letterSpacing:1,whiteSpace:"nowrap"}}>
                    + GRANT
                  </button>
                </div>
                {adminMsg&&(
                  <div style={{fontFamily:"monospace",fontSize:11,marginTop:10,
                    color:adminMsg.startsWith("✓")?"#22c55e":adminMsg.startsWith("Removed")?"#f59e0b":WARN}}>
                    {adminMsg}
                  </div>
                )}
              </div>

              {/* Current admins list */}
              <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
                borderRadius:12,padding:"18px"}}>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:14}}>
                  CURRENT ADMINS ({adminList.length})
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {adminList.map((a,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:12,
                      padding:"10px 14px",background:"rgba(255,165,0,0.04)",
                      border:"1px solid rgba(255,165,0,0.15)",borderRadius:10}}>
                      <span style={{fontSize:14}}>⚡</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,color:"#f59e0b",fontWeight:600}}>{a.email}</div>
                        {a.created_at&&(
                          <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",marginTop:2}}>
                            Added {new Date(a.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                          </div>
                        )}
                      </div>
                      <button onClick={()=>{AudioEngine.click();removeAdmin(a.email)}}
                        style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.2)",
                          color:WARN,borderRadius:6,padding:"4px 10px",fontFamily:"monospace",
                          fontSize:9,cursor:"pointer",letterSpacing:1}}>
                        REMOVE
                      </button>
                    </div>
                  ))}
                  {adminList.length===0&&(
                    <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",textAlign:"center",padding:20}}>
                      No admins found
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


